import React from 'react';
import { Checkbox } from '@mui/material';
import {Images, renderGoalTypes, tagEnums} from 'utils';
import {DateRow, FilterTags, SimpleTooltip, SkillCard, TagBox, TagComponent, TextRow} from 'components';
import { GoalStatusRow } from 'fragments';
import { goalStatusEnums, goalTypeEnums } from '../../../fragments/client/clientSingle/clientClinical/goals/constants';
import {canReadTags} from "../../../store/tags/tag.utils";

const goalTypeFilters = [
    { name: 'All', id: 'All' },
    { name: 'Behavior', id: goalTypeEnums.BEHAVIOR },
    { name: 'Skill', id: goalTypeEnums.SKILL },
    { name: 'Parent Training', id: goalTypeEnums.PARENT_TRAINING },
];

const goalStatusFilters = [
    { name: 'All', id: 'All' },
    { name: 'Hold', id: goalStatusEnums.HOLD },
    { name: 'Active', id: goalStatusEnums.ACTIVE },
    { name: 'Mastered', id: goalStatusEnums.MASTERED },
    { name: 'Discontinued', id: goalStatusEnums.DISCONTINUED },
    { name: 'Completed', id: goalStatusEnums.COMPLETED },
];

const stopRowToggle = (e) => e.stopPropagation();

export const goalsHead = (handleSelect, fullList, selected, domainList) => {
    return [
        {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#347AF0' }}
                        checked={fullList?.length === selected?.length}
                    />
                </SimpleTooltip>
            ),
            width: '50px',
        },
        { name: 'name', title: 'Goal Name' },
        {
            name: 'domains',
            title: 'Domain',
            disabled: true,
            multiple: true,
            filterList: [{ name: 'All', id: 'All' }, ...domainList],
        },
        { name: 'createdAt', title: 'Created', custom: false },
        { name: 'type', title: 'Type', filterList: goalTypeFilters },
        { name: 'dataMethod', title: 'Data Method', custom: false },
        canReadTags() && {name: 'tags', title: 'Tags', custom: false},
        { name: 'statuses', title: 'Status', disabled: true, multiple: true, filterList: goalStatusFilters },
    ];
};

export const goalsBody = (handleSelect, fullList, selected) => {
    return [
        {button: (handle, item) => (
                <Checkbox
                    onClick={(e) => handleSelect(e, item)}
                    name={'checkBox'}
                    style={{
                        padding: 0,
                        color: '#347AF0',
                    }}
                    checked={selected?.some((selectedItem) => selectedItem.id === item?.id)}
                />
            ),
            notClickable: true,
        },
        { rowText: (item) => <TextRow name={item?.name} /> },
        { rowText: (item) => <TextRow name={item?.domain?.name} /> },
        { rowText: (item) => <DateRow date={item?.createdAt} /> },
        { rowText: (item) => <SkillCard type={item?.type} /> },
        { rowText: (item) => <div className="full-width">{renderGoalTypes(item?.dataType)}</div> },
        canReadTags() && {
            rowText: (item) => <div className={{position:'relative'}}><TagBox tags={item?.tags} count={1} wrapperStyles={{marginBottom: 0}} /></div>
        },
        { rowText: (item) => <div className="full-width">{item?.status ? <GoalStatusRow status={item?.status} /> : '--'}</div> },
    ];
};

/**
 * The goals table as an accordion (`AccordionFullTable`), for Goal Mode tables with Target Mode
 * on: each goal opens its targets, like "Choose from Library". Same columns and filters as
 * `goalsHead` / `goalsBody`, plus a Targets count. `goalState(goal)` is 'none' | 'some' | 'all'.
 */
export const goalsWithTargetsTableInfo = ({ goals, domainList, goalState, targetCount, toggleGoal, toggleAll }) => {
    const states = (goals ?? []).map(goalState);
    const allPicked = !!states.length && states.every((state) => state === 'all');
    const somePicked = !allPicked && states.some((state) => state !== 'none');

    return {
        loadingType: ACTION_TYPE,
        head: [
            {
                renderView: (
                    <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                        <Checkbox
                            onChange={(e) => toggleAll(e.target.checked)}
                            name={'checkBox'}
                            // style={{ padding: 0, color: '#0C66E4' }}
                            style={{ color: '#0C66E4' }}
                            checked={allPicked}
                            indeterminate={somePicked}
                            disabled={!states.length}
                        />
                    </SimpleTooltip>
                ),
                width: '55px',
            },
            { name: 'name', title: 'Goal Name', width: '20%' },
            { name: 'targets', title: 'Targets', custom: false, disabled: true, width: '10%' },
            {
                name: 'domains',
                title: 'Domain',
                disabled: true,
                multiple: true,
                filterList: [{ name: 'All', id: 'All' }, ...domainList],
                width: '15%',
            },
            { name: 'createdAt', title: 'Created', custom: false, width: '10%' },
            { name: 'type', title: 'Type', filterList: goalTypeFilters, width: '10%' },
            { name: 'dataMethod', title: 'Data Method', custom: false, width: '10%' },
            canReadTags() && { name: 'tags', title: 'Tags', custom: false, width: '10%' },
            { name: 'statuses', title: 'Status', disabled: true, multiple: true, filterList: goalStatusFilters, width: '11%' },
        ],

        body: (item) => {
            const state = goalState(item);
            const { picked, total } = targetCount(item);
            return [
                {
                    value: (
                        <div onClick={stopRowToggle} style={{ padding: 16 }}>
                            <Checkbox
                                onChange={(e) => toggleGoal(item, e.target.checked)}
                                name={'checkBox'}
                                style={{ padding: 0, color: '#0C66E4' }}
                                checked={state === 'all'}
                                indeterminate={state === 'some'}
                            />
                        </div>
                    ),
                    styles: { width: '55px', padding: 0 },
                },
                { value: <TextRow name={item?.name} />, width: '20%' },
                { value: <TextRow name={picked ? `${picked} / ${total}` : `${total}`} />, width: '10%' },
                { value: <TextRow name={item?.domain?.name} />, width: '15%' },
                { value: <DateRow date={item?.createdAt} />, width: '10%' },
                { value: <SkillCard type={item?.type} />, width: '10%' },
                { value: <TextRow name={renderGoalTypes(item?.dataType)} />, width: '10%' },
                canReadTags() && { value: <TagBox tags={item?.tags} count={1} wrapperStyles={{ marginBottom: 0 }} />, width: '10%' },
                { value: item?.status ? <GoalStatusRow status={item?.status} /> : '--', width: '11%' },
            ];
        },

        notYet: goalNotYet,
    };
};

export const goalNotYet = {
    title: 'No Goals Information',
    subTitle: 'Start by adding your first Goal to manage their information.',
    image: Images.noClientYet,
};

export const ACTION_TYPE = 'GET_SIMPLE_GOALS';
